import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Select } from "../../components/ui-modal";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import AuthImg from "../../components/AuthImg";
import AuthFileLink from "../../components/AuthFileLink";
import SectionHeader from "../../components/SectionHeader";

const isImg = (name = "") => /\.(png|jpe?g|webp|svg)$/i.test(name);

export default function AdminIdentity() {
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [cid, setCid] = useState(searchParams.get("client") || "");
  const [ident, setIdent] = useState(null);
  const [colorNew, setColorNew] = useState({ name: "", hex: "#33338A" });
  const [typoNew, setTypoNew] = useState({ name: "", usage: "" });
  const [idKind, setIdKind] = useState("logo");
  const idFileRef = useRef();

  useEffect(() => { api.get("/admin/clients").then((r) => setClients(r.data)); }, []);
  const loadIdent = (id) => api.get(`/admin/clients/${id}/identity`).then((r) => setIdent(r.data));
  useEffect(() => { if (cid) loadIdent(cid); else setIdent(null); }, [cid]);

  const persist = async (next) => { setIdent(next); await api.put(`/admin/clients/${cid}/identity`, { palette: next.palette, typography: next.typography, rules: next.rules }); };
  const addColor = async () => { if (!colorNew.name) return; await persist({ ...ident, palette: [...(ident.palette || []), colorNew] }); setColorNew({ name: "", hex: "#33338A" }); toast.success("Couleur ajoutée"); };
  const delColor = async (i) => { await persist({ ...ident, palette: ident.palette.filter((_, x) => x !== i) }); };
  const addTypo = async () => { if (!typoNew.name) return; await persist({ ...ident, typography: [...(ident.typography || []), typoNew] }); setTypoNew({ name: "", usage: "" }); toast.success("Typographie ajoutée"); };
  const delTypo = async (i) => { await persist({ ...ident, typography: ident.typography.filter((_, x) => x !== i) }); };

  const uploadIdFile = async (e) => { const f = e.target.files[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); fd.append("kind", idKind); fd.append("label", f.name); try { await api.post(`/admin/clients/${cid}/identity/files`, fd); toast.success("Fichier ajouté"); loadIdent(cid); }
    catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible, réessayez."); } finally { e.target.value = ""; } };
  const delFile = async (path) => { await api.delete(`/admin/clients/${cid}/identity/files`, { params: { path } }); toast.success("Fichier supprimé"); loadIdent(cid); };

  const logos = ident?.logos || [];
  const palette = ident?.palette || [];
  const typography = ident?.typography || [];
  const kitCharte = (ident?.files || []).filter((f) => f.kind === "kit" || f.kind === "charte");

  return (
    <div className="max-w-4xl">
      <PageTitle overline="CMS · Charte de marque" title="Identité Visuelle" />
      <div className="max-w-sm mb-8">
        <Select label="Entreprise" value={cid} onChange={setCid} options={[{ v: "", l: "— Sélectionner une entreprise —" }, ...clients.map((c) => ({ v: c.id, l: c.name }))]} testid="identity-client-select" />
      </div>

      {!cid && <p className="text-sm text-marine/80">Sélectionnez une entreprise pour visualiser et éditer sa charte de marque (logo, couleurs, typographies).</p>}

      {cid && ident && (
        <div className="space-y-14">
          {/* LOGO */}
          <section id="logos">
            <div className="flex items-end justify-between">
              <SectionHeader over="Logo" title="Le logo" />
              <div className="flex gap-2 items-center mb-6">
                <select value={idKind} onChange={(e) => setIdKind(e.target.value)} data-testid="id-file-kind" className="inp max-w-[150px]">
                  <option value="logo">Logo</option><option value="kit">Kit complet</option><option value="charte">Charte PDF</option>
                </select>
                <button onClick={() => idFileRef.current.click()} data-testid="id-file-upload" className="bg-marine text-white rounded-sm px-4 py-2 text-sm hover:bg-marine-hover transition-colors flex items-center gap-1"><Icons.Upload size={16} /> Uploader</button>
                <input ref={idFileRef} type="file" className="hidden" onChange={uploadIdFile} />
              </div>
            </div>
            {logos.length === 0 ? <p className="text-sm text-marine/80">Aucun logo. Sélectionnez « Logo » ci-dessus puis uploadez le fichier.</p> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {logos.map((l, i) => (
                  <div key={i} data-testid={`admin-logo-${i}`} className="rounded-sm border border-marine/10 overflow-hidden bg-sable group relative">
                    <div className={`h-40 flex items-center justify-center p-6 ${i % 2 ? "bg-marine" : "beige-texture"}`}>
                      {l.path && isImg(l.name || l.path) ? <AuthImg path={l.path} alt={l.label} className="max-h-full max-w-full object-contain" /> : <Icons.Image size={16} className="text-marine/30" />}
                    </div>
                    <div className="p-3 flex items-center justify-between">
                      <span className="text-sm text-marine truncate">{l.label || l.name}</span>
                      <button onClick={() => delFile(l.path)} data-testid={`del-logo-${i}`} className="text-rouge shrink-0 ml-2 hover:opacity-70"><Icons.Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* COULEURS */}
          <section id="couleurs">
            <SectionHeader over="Couleurs" title="La palette" />
            {palette.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-5">
                {palette.map((c, i) => (
                  <div key={i} data-testid={`admin-color-${i}`} className="rounded-sm border border-marine/10 overflow-hidden bg-sable group relative">
                    <div className="h-28" style={{ background: c.hex }} />
                    <div className="p-3 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-marine truncate">{c.name}</div>
                        <div className="text-xs text-marine/80 font-mono uppercase">{c.hex}</div>
                      </div>
                      <button onClick={() => delColor(i)} data-testid={`del-color-${i}`} className="text-rouge shrink-0 ml-2 hover:opacity-70"><Icons.Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-center bg-sable border border-marine/10 rounded-sm p-3 max-w-xl">
              <input placeholder="Nom (ex: Bleu Marine)" value={colorNew.name} onChange={(e) => setColorNew({ ...colorNew, name: e.target.value })} data-testid="color-name" className="inp" />
              <input type="color" value={colorNew.hex} onChange={(e) => setColorNew({ ...colorNew, hex: e.target.value })} data-testid="color-hex" className="w-12 h-10 rounded-sm border border-marine/20 shrink-0" />
              <button onClick={addColor} data-testid="color-add" className="border border-marine text-marine rounded-sm px-4 py-2 text-sm hover:bg-marine hover:text-white transition-colors whitespace-nowrap">+ Couleur</button>
            </div>
          </section>

          {/* TYPOGRAPHIES */}
          <section id="typographie">
            <SectionHeader over="Typographie" title="Les typographies" />
            {typography.length > 0 && (
              <div className="space-y-3 mb-5">
                {typography.map((t, i) => {
                  const serif = /playfair|serif|georgia|times/i.test(t.name);
                  return (
                    <div key={i} data-testid={`admin-typo-${i}`} className="bg-sable rounded-sm border border-marine/10 p-6 relative">
                      <div className="flex items-baseline justify-between mb-3">
                        <div className="text-xs text-bordeaux uppercase tracking-wider">{t.usage || "Usage"}</div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-marine/80">{t.name}</span>
                          <button onClick={() => delTypo(i)} data-testid={`del-typo-${i}`} className="text-rouge hover:opacity-70"><Icons.Trash2 size={16} /></button>
                        </div>
                      </div>
                      <div className={`text-4xl text-marine mb-1 ${serif ? "font-display" : "font-sans"}`}>{t.name}</div>
                      <div className={`text-lg text-gris-texte ${serif ? "font-display" : "font-sans"}`}>Aa Bb Cc Dd · 0 1 2 3 4 5 6 7 8 9</div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex gap-2 items-center bg-sable border border-marine/10 rounded-sm p-3 max-w-xl">
              <input placeholder="Police (ex: Playfair Display)" value={typoNew.name} onChange={(e) => setTypoNew({ ...typoNew, name: e.target.value })} data-testid="typo-name" className="inp" />
              <input placeholder="Usage (ex: Titres)" value={typoNew.usage} onChange={(e) => setTypoNew({ ...typoNew, usage: e.target.value })} data-testid="typo-usage" className="inp max-w-[180px]" />
              <button onClick={addTypo} data-testid="typo-add" className="border border-marine text-marine rounded-sm px-4 py-2 text-sm hover:bg-marine hover:text-white transition-colors whitespace-nowrap">+ Police</button>
            </div>
          </section>

          {/* FICHIERS */}
          <section id="fichiers">
            <SectionHeader over="Livrables" title="Kit & charte PDF" />
            {kitCharte.length === 0 ? <p className="text-sm text-marine/80">Aucun fichier. Uploadez un « Kit complet » ou une « Charte PDF » via le bouton en haut de la page.</p> : (
              <div className="flex flex-wrap gap-2">
                {kitCharte.map((f, i) => (
                  <div key={i} data-testid={`admin-file-${i}`} className="flex items-center gap-3 border border-marine/10 rounded-sm p-3 bg-sable text-sm">
                    <Icons.File size={16} className="text-marine" />
                    <AuthFileLink path={f.path} className="hover:text-bordeaux">{f.label || f.name}</AuthFileLink>
                    <span className="text-xs text-bordeaux uppercase">{f.kind}</span>
                    <button onClick={() => delFile(f.path)} data-testid={`del-file-${i}`} className="text-rouge hover:opacity-70"><Icons.Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
